import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp, deleteDoc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoArrowBack, IoCarSportOutline, IoAddOutline, IoTrashOutline, IoCloseCircleOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import './EstacionamientosScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';

function EstacionamientosScreen() {
    const { userData, currentUser } = useAuth();
    const navigate = useNavigate();
    const [cajones, setCajones] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showAddCajonModal, setShowAddCajonModal] = useState(false);
    const [nuevoCajon, setNuevoCajon] = useState('');

    const [selectedCajon, setSelectedCajon] = useState(null);
    const [showAsignarModal, setShowAsignarModal] = useState(false);
    const [asignarData, setAsignarData] = useState({
        placaVehiculo: '',
        ocupadoPor: '',
        casaVisita: '',
    });
    const [casasDisponibles, setCasasDisponibles] = useState([]);
    const [showSolicitarModal, setShowSolicitarModal] = useState(false);
    const [solicitudPlaca, setSolicitudPlaca] = useState('');
    const [solicitudes, setSolicitudes] = useState([]);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showConfirmRejectModal, setShowConfirmRejectModal] = useState({ show: false, solicitud: null });
    const [showLiberarResidenteModal, setShowLiberarResidenteModal] = useState(false);

    const isManager = userData?.rol === 'admin' || userData?.rol === 'comite' || userData?.rol === 'vigilante';
    const isAdmin = userData?.rol === 'admin' || userData?.rol === 'comite';

    useEffect(() => {
        if (!userData?.condominioId) return;
        fetchCajones();
        fetchCasas();

        const qSolicitudes = query(
            collection(db, 'estacionamientos_solicitudes'),
            where('condominioId', '==', userData.condominioId),
            where('estatus', '==', 'pendiente')
        );
        const unsubscribe = onSnapshot(qSolicitudes, (snap) => {
            const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSolicitudes(list);
        });

        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    const fetchCasas = async () => {
        try {
            const q = query(collection(db, 'usuarios'), where('condominioId', '==', userData.condominioId));
            const snap = await getDocs(q);
            const casasSet = new Set();
            snap.docs.forEach(doc => {
                const data = doc.data();
                if (data.numeroCasa) casasSet.add(data.numeroCasa);
                if (data.residencia) casasSet.add(data.residencia);
            });
            setCasasDisponibles(Array.from(casasSet).sort());
        } catch (e) {
            console.error("Error fetching casas", e);
        }
    };

    const fetchCajones = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'estacionamientos'), where('condominioId', '==', userData.condominioId));
            const snapshot = await getDocs(q);
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            list.sort((a, b) => {
                const numA = parseInt(a.numeroCajon.replace(/\D/g, '')) || 0;
                const numB = parseInt(b.numeroCajon.replace(/\D/g, '')) || 0;
                if (numA === numB) return a.numeroCajon.localeCompare(b.numeroCajon);
                return numA - numB;
            });

            setCajones(list);
        } catch (error) {
            console.error("Error fetching estacionamientos:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCrearCajon = async (e) => {
        e.preventDefault();
        if (!nuevoCajon.trim()) return;

        try {
            await addDoc(collection(db, 'estacionamientos'), {
                condominioId: userData.condominioId,
                numeroCajon: nuevoCajon.toUpperCase(),
                estado: 'libre',
                ocupadoPor: '',
                placaVehiculo: '',
                horaLlegada: null
            });
            setNuevoCajon('');
            setShowAddCajonModal(false);
            fetchCajones();
        } catch (error) {
            console.error("Error creando cajón:", error);
            alert("Error al crear el cajón.");
        }
    };

    const handleEliminarCajon = async (id) => {
        if (!window.confirm("¿Estás seguro de eliminar este cajón del sistema?")) return;
        try {
            await deleteDoc(doc(db, 'estacionamientos', id));
            fetchCajones();
        } catch (error) {
            console.error("Error eliminando cajón", error);
        }
    };

    const isCajonMio = (cajon) => {
        if (!cajon || cajon.estado !== 'ocupado') return false;
        const uid = currentUser?.uid || userData?.uid || userData?.id;
        if (cajon.residenteId && uid && cajon.residenteId === uid) return true;
        if (cajon.casaVisita && userData?.numeroCasa && cajon.casaVisita.trim().toLowerCase() === userData.numeroCasa.trim().toLowerCase()) return true;
        if (cajon.ocupadoPor && userData?.nombre && cajon.ocupadoPor.trim().toLowerCase() === userData.nombre.trim().toLowerCase()) return true;
        return false;
    };

    const openCajonModal = (cajon) => {
        if (!isManager && userData?.rol !== 'residente') return;

        setSelectedCajon(cajon);
        if (userData?.rol === 'residente') {
            if (cajon.estado === 'libre') {
                setSolicitudPlaca('');
                setShowSolicitarModal(true);
            } else if (isCajonMio(cajon)) {
                setShowLiberarResidenteModal(true);
            } else {
                alert("Este cajón ya está ocupado por otra visita.");
            }
            return;
        }

        if (cajon.estado === 'libre') {
            setAsignarData({ placaVehiculo: '', ocupadoPor: '', casaVisita: '' });
        }
        setShowAsignarModal(true);
    };

    const handleAsignarCajon = async (e) => {
        e.preventDefault();
        if (!selectedCajon) return;

        try {
            const ref = doc(db, 'estacionamientos', selectedCajon.id);
            await updateDoc(ref, {
                estado: 'ocupado',
                placaVehiculo: (asignarData.placaVehiculo || '').toUpperCase().trim(),
                ocupadoPor: asignarData.ocupadoPor || 'Visita',
                casaVisita: asignarData.casaVisita || 'N/A',
                horaLlegada: serverTimestamp()
            });
            setShowAsignarModal(false);
            fetchCajones();
        } catch (error) {
            console.error("Error asignando cajón:", error);
            alert("Error al asignar el cajón.");
        }
    };

    const handleLiberarCajon = async () => {
        if (!selectedCajon) return;
        if (!window.confirm("¿Confirmas que el vehículo ya se retiró y el cajón está libre?")) return;

        try {
            const ref = doc(db, 'estacionamientos', selectedCajon.id);
            await updateDoc(ref, {
                estado: 'libre',
                placaVehiculo: '',
                ocupadoPor: '',
                casaVisita: '',
                residenteId: '',
                solicitudId: '',
                horaLlegada: null
            });
            if (selectedCajon.solicitudId) {
                try {
                    await updateDoc(doc(db, 'estacionamientos_solicitudes', selectedCajon.solicitudId), {
                        estatus: 'liberada',
                        fechaLiberado: serverTimestamp(),
                        liberadoPor: 'admin'
                    });
                } catch (e) {
                    console.warn("No se pudo actualizar solicitud:", e);
                }
            }
            setShowAsignarModal(false);
            fetchCajones();
        } catch (error) {
            console.error("Error liberando cajón:", error);
            alert("Error al liberar el cajón.");
        }
    };

    const handleLiberarResidente = async () => {
        if (!selectedCajon) return;

        try {
            const refCajon = doc(db, 'estacionamientos', selectedCajon.id);
            await updateDoc(refCajon, {
                estado: 'libre',
                placaVehiculo: '',
                ocupadoPor: '',
                casaVisita: '',
                residenteId: '',
                solicitudId: '',
                horaLlegada: null
            });

            if (selectedCajon.solicitudId) {
                try {
                    await updateDoc(doc(db, 'estacionamientos_solicitudes', selectedCajon.solicitudId), {
                        estatus: 'liberada',
                        fechaLiberado: serverTimestamp(),
                        liberadoPor: 'residente'
                    });
                } catch (e) {
                    console.warn("No se pudo actualizar la solicitud asociada:", e);
                }
            }

            setShowLiberarResidenteModal(false);
            fetchCajones();
        } catch (error) {
            console.error("Error liberando cajón de visita:", error);
            alert("Error al liberar el cajón.");
        }
    };

    const handleSolicitarCajon = async (e) => {
        e.preventDefault();
        if (!selectedCajon || !solicitudPlaca.trim()) return;

        const uid = currentUser?.uid || userData?.uid || userData?.id || '';
        const condoId = userData?.condominioId || selectedCajon?.condominioId || '';

        try {
            await addDoc(collection(db, 'estacionamientos_solicitudes'), {
                cajonId: selectedCajon.id,
                numeroCajon: selectedCajon.numeroCajon || '',
                residenteId: uid,
                nombreResidente: userData?.nombre || currentUser?.displayName || 'Residente',
                casaVisita: userData?.numeroCasa || 'N/A',
                placaVehiculo: solicitudPlaca.toUpperCase().trim(),
                estatus: 'pendiente',
                fechaSolicitud: serverTimestamp(),
                condominioId: condoId
            });

            setShowSuccessModal(true);
            setShowSolicitarModal(false);
            setSolicitudPlaca('');
            fetchCajones();
        } catch (error) {
            console.error("Error al solicitar cajón:", error);
            alert("Error al enviar la solicitud.");
        }
    };

    const handleAprobarSolicitud = async (solicitud) => {
        try {
            await updateDoc(doc(db, 'estacionamientos_solicitudes', solicitud.id), {
                estatus: 'aprobada',
                fechaProcesado: serverTimestamp(),
                procesadoPor: userData?.nombre || currentUser?.displayName || 'Personal'
            });

            await updateDoc(doc(db, 'estacionamientos', solicitud.cajonId), {
                estado: 'ocupado',
                placaVehiculo: solicitud.placaVehiculo || '',
                ocupadoPor: solicitud.nombreResidente || 'Visita',
                casaVisita: solicitud.casaVisita || 'N/A',
                residenteId: solicitud.residenteId || '',
                solicitudId: solicitud.id || '',
                horaLlegada: serverTimestamp()
            });

            fetchCajones();
        } catch (error) {
            console.error("Error al aprobar solicitud:", error);
            alert("Error al aprobar la solicitud.");
        }
    };

    const handleRechazarSolicitud = (solicitud) => {
        setShowConfirmRejectModal({ show: true, solicitud });
    };

    const confirmRechazarSolicitud = async () => {
        const { solicitud } = showConfirmRejectModal;
        setShowConfirmRejectModal({ show: false, solicitud: null });
        if (!solicitud) return;
        try {
            await updateDoc(doc(db, 'estacionamientos_solicitudes', solicitud.id), {
                estatus: 'rechazada',
                fechaProcesado: serverTimestamp(),
                procesadoPor: userData?.nombre || currentUser?.displayName || 'Personal'
            });

            fetchCajones();
        } catch (error) {
            console.error("Error al rechazar solicitud:", error);
            alert("Error al rechazar la solicitud.");
        }
    };

    const libres = cajones.filter(c => c.estado === 'libre').length;
    const ocupados = cajones.length - libres;

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/estacionamientos" />
            <main className="ks-main">
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader showBack={false} />
                </div>
                <div className="ks-scroll-area">
                    <div className="muro-header-stitch">
                        <div className="muro-hero-card">
                            <div className="muro-hero-content">
                                <h1>Estacionamientos</h1>
                                <p>Cajones de visita</p>
                            </div>
                            <IoCarSportOutline className="muro-hero-icon-decor" />
                        </div>
                    </div>

                    <div className="estacionamientos-container">
                        <div className="estacionamientos-stats" style={{ marginTop: '15px', marginBottom: '10px' }}>
                                <div className="stat-card">
                                    <span className="stat-value">{cajones.length}</span>
                                    <span className="stat-label">Total</span>
                                </div>
                                <div className="stat-card libre">
                                    <span className="stat-value">{libres}</span>
                                    <span className="stat-label">Libres</span>
                                </div>
                                <div className="stat-card ocupado">
                                    <span className="stat-value">{ocupados}</span>
                                    <span className="stat-label">Ocupados</span>
                                </div>
                            </div>

                        {isManager && solicitudes.length > 0 && (
                            <div className="solicitudes-pendientes-section" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '16px', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#1e293b', marginTop: 0, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444', display: 'inline-block' }}></span>
                                    Solicitudes Pendientes ({solicitudes.length})
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {solicitudes.map(sol => (
                                        <div key={sol.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#ffffff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '12px 16px', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b' }}>
                                                    Cajón {sol.numeroCajon} — Casa {sol.casaVisita}
                                                </span>
                                                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                    Residente: {sol.nombreResidente} | Placas: <strong>{sol.placaVehiculo}</strong>
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button 
                                                    onClick={() => handleAprobarSolicitud(sol)} 
                                                    style={{ backgroundColor: '#22c55e', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer' }}
                                                >
                                                    Aprobar
                                                </button>
                                                <button 
                                                    onClick={() => handleRechazarSolicitud(sol)} 
                                                    style={{ backgroundColor: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer' }}
                                                >
                                                    Rechazar
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                            {isAdmin && (
                                <div style={{ marginBottom: '5px', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button className="btn-agregar-cajon" onClick={() => setShowAddCajonModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 15px', borderRadius: '10px', background: '#4c1d95', color: 'white', border: 'none', fontWeight: 600, fontSize: '0.85rem' }}>
                                        <IoAddOutline size={18} /> Agregar Cajón
                                    </button>
                                </div>
                            )}
                        <div className="estacionamientos-content" style={{ paddingTop: '10px' }}>

                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}><div className="loader"></div></div>
                            ) : cajones.length === 0 ? (
                                <div className="empty-state">
                                    <IoCarSportOutline size={48} color="#9ca3af" />
                                    <h3>Sin cajones configurados</h3>
                                    <p>{isAdmin ? "Agrega el primer cajón de visitas." : "Aún no hay cajones configurados."}</p>
                                </div>
                            ) : (
                                <div className="estacionamientos-grid">
                                    {cajones.map(cajon => (
                                        <div
                                            key={cajon.id}
                                            className={`cajon-card ${cajon.estado} ${(isManager || (userData?.rol === 'residente' && (cajon.estado === 'libre' || isCajonMio(cajon)))) ? 'clickable' : ''}`}
                                            onClick={() => openCajonModal(cajon)}
                                        >
                                            <div className="cajon-header">
                                                <h3>{cajon.numeroCajon}</h3>
                                                {isAdmin && cajon.estado === 'libre' && (
                                                    <button
                                                        className="btn-delete-cajon"
                                                        onClick={(e) => { e.stopPropagation(); handleEliminarCajon(cajon.id); }}
                                                    >
                                                        <IoTrashOutline />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="cajon-body">
                                                <div className={`status-badge ${cajon.estado}`}>
                                                    {cajon.estado}
                                                </div>
                                                {isCajonMio(cajon) && (
                                                    <span style={{
                                                        fontSize: '0.72rem',
                                                        background: '#ecfdf5',
                                                        color: '#059669',
                                                        border: '1px solid #a7f3d0',
                                                        padding: '3px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: '700',
                                                        marginTop: '6px',
                                                        display: 'inline-block',
                                                        textAlign: 'center'
                                                    }}>
                                                        Tu Visita (Liberar)
                                                    </span>
                                                )}
                                                {cajon.estado === 'ocupado' && (
                                                    <div className="cajon-details">
                                                        <p className="placa">{cajon.placaVehiculo || 'S/P'}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {showAddCajonModal && (
                            <div className="modal-overlay" onClick={() => setShowAddCajonModal(false)}>
                                <div className="modal-content" onClick={e => e.stopPropagation()}>
                                    <h3>Agregar Cajón</h3>
                                    <form onSubmit={handleCrearCajon}>
                                        <input
                                            type="text"
                                            value={nuevoCajon}
                                            onChange={e => setNuevoCajon(e.target.value)}
                                            placeholder="Ej: V-01"
                                            className="modal-input"
                                            required
                                        />
                                        <button type="submit" className="btn-save">Crear</button>
                                    </form>
                                </div>
                            </div>
                        )}

                        {showAsignarModal && selectedCajon && (
                            <div className="modal-overlay" onClick={() => setShowAsignarModal(false)}>
                                <div className="modal-content" onClick={e => e.stopPropagation()}>
                                    <h3>Cajón {selectedCajon.numeroCajon}</h3>
                                    {selectedCajon.estado === 'libre' ? (
                                        <form onSubmit={handleAsignarCajon}>
                                            <div className="form-group">
                                                <label>Placas</label>
                                                <input
                                                    type="text"
                                                    value={asignarData.placaVehiculo}
                                                    onChange={e => setAsignarData({ ...asignarData, placaVehiculo: e.target.value })}
                                                    className="modal-input"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Casa</label>
                                                <select
                                                    value={asignarData.casaVisita}
                                                    onChange={e => setAsignarData({ ...asignarData, casaVisita: e.target.value })}
                                                    className="modal-input"
                                                    required
                                                >
                                                    <option value="">Seleccionar...</option>
                                                    {casasDisponibles.map((casa, idx) => (
                                                        <option key={idx} value={casa}>{casa}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <button type="submit" className="btn-save">Asignar</button>
                                        </form>
                                    ) : (
                                        <div>
                                            <p><strong>Placas:</strong> {selectedCajon.placaVehiculo}</p>
                                            <p><strong>Visita a:</strong> {selectedCajon.casaVisita}</p>
                                            <button onClick={handleLiberarCajon} className="btn-liberar">Liberar Cajón</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {showSolicitarModal && selectedCajon && (
                            <div className="modal-overlay" onClick={() => setShowSolicitarModal(false)}>
                                <div className="modal-content" onClick={e => e.stopPropagation()}>
                                    <h3>Solicitar Cajón {selectedCajon.numeroCajon}</h3>
                                    <form onSubmit={handleSolicitarCajon}>
                                        <div className="form-group">
                                            <label>Placas del Vehículo</label>
                                            <input
                                                type="text"
                                                value={solicitudPlaca}
                                                onChange={e => setSolicitudPlaca(e.target.value)}
                                                placeholder="Ej: ABC-123-A"
                                                className="modal-input"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Casa Solicitante</label>
                                            <input
                                                type="text"
                                                value={userData?.numeroCasa || 'N/A'}
                                                className="modal-input"
                                                disabled
                                            />
                                        </div>
                                        <button type="submit" className="btn-save">Enviar Solicitud</button>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {/* ── FLOATING ISLAND BOTTOM NAV (mobile) ── */}
                <KondoBottomNav activeRoute="/estacionamientos" />

                {showSuccessModal && (
                    <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, padding: '20px' }}>
                        <div className="modal-content" style={{ backgroundColor: '#ffffff', borderRadius: '24px', padding: '28px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <div style={{ backgroundColor: '#ecfdf5', borderRadius: '50%', padding: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                                <IoCarSportOutline size={40} />
                            </div>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0', fontFamily: 'Manrope, sans-serif' }}>Solicitud Enviada</h3>
                            <p style={{ fontSize: '0.92rem', color: '#64748b', lineHeight: '1.5', margin: '0 0 24px 0' }}>
                                Tu solicitud de cajón de visitas ha sido enviada con éxito. Vigilancia y Administración han sido notificadas de inmediato para proceder con la asignación.
                            </p>
                            <button
                                onClick={() => setShowSuccessModal(false)}
                                style={{ backgroundColor: '#7B61FF', color: '#ffffff', border: 'none', borderRadius: '16px', padding: '14px 24px', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer', width: '100%', boxShadow: '0 4px 6px -1px rgba(123, 97, 255, 0.2)' }}
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                )}

                {showConfirmRejectModal.show && showConfirmRejectModal.solicitud && (
                    <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, padding: '20px' }}>
                        <div className="modal-content" style={{ backgroundColor: '#ffffff', borderRadius: '24px', padding: '28px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <div style={{ backgroundColor: '#fff5f5', borderRadius: '50%', padding: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                                <IoCloseCircleOutline size={40} />
                            </div>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0', fontFamily: 'Manrope, sans-serif' }}>Rechazar Solicitud</h3>
                            <p style={{ fontSize: '0.92rem', color: '#64748b', lineHeight: '1.5', margin: '0 0 24px 0' }}>
                                ¿Confirmas que deseas rechazar la solicitud del cajón <strong>{showConfirmRejectModal.solicitud.numeroCajon}</strong> de la casa <strong>{showConfirmRejectModal.solicitud.casaVisita}</strong>?
                            </p>
                            <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                                <button
                                    onClick={() => setShowConfirmRejectModal({ show: false, solicitud: null })}
                                    style={{ flex: 1, backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '16px', padding: '14px 24px', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmRechazarSolicitud}
                                    style={{ flex: 1, backgroundColor: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '16px', padding: '14px 24px', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.2)' }}
                                >
                                    Rechazar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showLiberarResidenteModal && selectedCajon && (
                    <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, padding: '20px' }}>
                        <div className="modal-content" style={{ backgroundColor: '#ffffff', borderRadius: '24px', padding: '28px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <div style={{ backgroundColor: '#ecfdf5', borderRadius: '50%', padding: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                                <IoCarSportOutline size={40} />
                            </div>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e293b', margin: '0 0 8px 0', fontFamily: 'Manrope, sans-serif' }}>
                                Cajón {selectedCajon.numeroCajon}
                            </h3>
                            <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '14px 20px', width: '100%', marginBottom: '18px', border: '1px solid #e2e8f0', textAlign: 'left' }}>
                                <p style={{ margin: '0 0 6px 0', fontSize: '0.9rem', color: '#64748b' }}>
                                    <strong>Placas:</strong> <span style={{ color: '#1e293b', fontWeight: 700 }}>{selectedCajon.placaVehiculo || 'S/P'}</span>
                                </p>
                                <p style={{ margin: '0 0 6px 0', fontSize: '0.9rem', color: '#64748b' }}>
                                    <strong>Visita de:</strong> <span style={{ color: '#1e293b', fontWeight: 600 }}>{selectedCajon.ocupadoPor || 'Tu Visita'} ({selectedCajon.casaVisita})</span>
                                </p>
                                <p style={{ margin: '0', fontSize: '0.82rem', color: '#10b981', fontWeight: 600 }}>
                                    ● Cajón otorgado para tu visita
                                </p>
                            </div>
                            <p style={{ fontSize: '0.92rem', color: '#64748b', lineHeight: '1.5', margin: '0 0 24px 0' }}>
                                ¿Tu visita ya se retiró? Al liberar el cajón, quedará disponible inmediatamente para que otros vecinos puedan solicitarlo.
                            </p>
                            <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                                <button
                                    onClick={() => setShowLiberarResidenteModal(false)}
                                    style={{ flex: 1, backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '16px', padding: '14px 20px', fontWeight: '700', fontSize: '0.92rem', cursor: 'pointer' }}
                                >
                                    Aún en uso
                                </button>
                                <button
                                    onClick={handleLiberarResidente}
                                    style={{ flex: 1.2, backgroundColor: '#10b981', color: '#ffffff', border: 'none', borderRadius: '16px', padding: '14px 20px', fontWeight: '700', fontSize: '0.92rem', cursor: 'pointer', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' }}
                                >
                                    Liberar Cajón
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default EstacionamientosScreen;
